/**
 * Manifold plan names, split into the two dimensions they actually carry.
 *
 * A plan name is a Chargebee entity id — "Semaglutide-Level-3-USD-Monthly",
 * "GCI-3Month-USD-Yearly", "Classic-Capsules-USD-Monthly" — and it encodes
 * three things at once: what the patient is taking, how often they are billed,
 * and how much supply arrives per shipment. The Payments page used to strip the
 * suffix off and plot the remainder, which produced a slice per SKU: eleven
 * wedges that answered neither "what are we selling" nor "how are people
 * paying".
 *
 * MOLECULE IS NOT ALWAYS IN THE NAME. Four product families are branded rather
 * than described, and no amount of parsing recovers the drug from them:
 *
 *   GCI / GCODT / Game-Changer-*  →  Semaglutide  (Game Changer, injectable & ODT)
 *   Ultimate                      →  Tirzepatide
 *   Classic                       →  Phentermine
 *
 * That mapping is product knowledge, confirmed 2026-09-09, and it is 49,258
 * orders on MDExam — 18% of the catalogue. It cannot be derived, only recorded,
 * which is why it lives here as data and not as a clever regex. A new branded
 * family added upstream will fall to `null` and show as Unclassified rather
 * than being quietly folded into whichever pattern matched first.
 */

/** Branded families whose molecule the name does not state. Order matters: the
 *  longest, most specific prefix must be tested first, or `gc` would swallow
 *  `gcodt`. */
const BRANDED_FAMILIES = [
  { pattern: /^gcodt/, drug: 'Semaglutide' },
  { pattern: /^gci/, drug: 'Semaglutide' },
  { pattern: /^game-changer/, drug: 'Semaglutide' },
  { pattern: /^ultimate/, drug: 'Tirzepatide' },
  { pattern: /^classic/, drug: 'Phentermine' },
]

/** Families the name does describe, matched anywhere in the string. */
const NAMED_DRUGS = [
  // Brand-name management programs name the brand, not the molecule.
  { pattern: /semaglutide|wegovy|ozempic|rybelsus/, drug: 'Semaglutide' },
  { pattern: /tirzepatide|zepbound|mounjaro/, drug: 'Tirzepatide' },
  { pattern: /phentermine/, drug: 'Phentermine' },
  // Foundayo is Lilly's oral orforglipron.
  { pattern: /foundayo|orforglipron/, drug: 'Orforglipron' },
  { pattern: /minoxidil|finasteride/, drug: 'Hair Loss' },
  { pattern: /^nad/, drug: 'NAD+' },
  { pattern: /^trt/, drug: 'Testosterone' },
  { pattern: /b12/, drug: 'B12' },
  // Last: a plan that only says "GLP1" is a real product, but it does not say
  // WHICH, and folding it into Semaglutide would invent a fact.
  { pattern: /glp1/, drug: 'GLP-1 (unspecified)' },
]

/**
 * The molecule a plan sells, or null when the name does not say and no branded
 * family claims it.
 * @param {string} planName
 * @returns {string|null}
 */
export function drugOf(planName) {
  if (!planName || typeof planName !== 'string') return null
  const name = planName.trim().toLowerCase()
  if (!name) return null

  // Branded families first: "Ultimate-USD-Monthly" contains no molecule, and a
  // name like "Semaglutide-..." is unambiguous either way.
  for (const { pattern, drug } of BRANDED_FAMILIES) {
    if (pattern.test(name)) return drug
  }
  for (const { pattern, drug } of NAMED_DRUGS) {
    if (pattern.test(name)) return drug
  }
  return null
}

/**
 * How often the subscription bills, from the suffix.
 *
 * NOT the same as how much supply arrives, and the two genuinely disagree:
 * 22,192 MDExam orders are a 3-month supply billed Yearly. Billing frequency is
 * the one this reports, because it is what the plan name reliably carries —
 * supply size is present on only 15% of plans and its 6- and 12-month variants
 * total 35 orders against 265,000.
 *
 * @param {string} planName
 * @returns {string|null}
 */
export function billingFrequencyOf(planName) {
  if (!planName || typeof planName !== 'string') return null
  const name = planName.trim().toLowerCase()
  if (/monthly$/.test(name)) return 'Monthly'
  if (/quarterly$/.test(name)) return 'Quarterly'
  if (/yearly$|annual(ly)?$/.test(name)) return 'Yearly'
  if (/weekly$/.test(name)) return 'Weekly'
  return null
}

/**
 * Roll a Manifold `subscriptions` array up along one dimension.
 *
 * Anything the classifier returns null for becomes "Unclassified" rather than
 * being dropped: a slice that silently omits plans would make the donut add to
 * less than the total it sits next to, and nobody would notice which.
 *
 * @param {Array<{name?: string, num_of_subscriptions?: number}>} subscriptions
 * @param {(name: string) => string|null} classify
 * @returns {Array<{name: string, count: number, percentage: number}>}
 */
export function groupPlans(subscriptions, classify) {
  if (!Array.isArray(subscriptions)) return []

  const totals = new Map()
  for (const row of subscriptions) {
    const key = classify(row?.name) || 'Unclassified'
    totals.set(key, (totals.get(key) || 0) + (row?.num_of_subscriptions || 0))
  }

  const total = [...totals.values()].reduce((sum, n) => sum + n, 0)
  return [...totals.entries()]
    .filter(([, count]) => count > 0)
    .map(([name, count]) => ({
      name,
      count,
      percentage: total > 0 ? (100 * count) / total : 0,
    }))
    .sort((a, b) => b.count - a.count)
}
